import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import { useNavigate } from "react-router-dom";
import {
  Check, Lock, BookOpen, ClipboardList, BarChart2,
  Shield, Star, RefreshCw, ChevronDown, ChevronUp,
} from "lucide-react";
import { useState } from "react";

const MONTHLY_PRICE = 79;

const FREE_FEATURES = [
  "10 øvingsspørsmål per dag",
  "3 skiltgjenkjenningsspørsmål",
  "Grunnleggende fremdriftssporing",
  "Norsk, Somali og Arabisk",
];

const PREMIUM_FEATURES = [
  "Ubegrensede øvingsspørsmål",
  "Full prøveeksamen (45 spørsmål)",
  "Alt innhold om skilt og vegoppmerking",
  "Detaljerte forklaringer med lovreferanser",
  "Fremdriftsanalyse og oppdaging av svake tema",
  "Norsk, Somali og Arabisk",
];

const FAQS = [
  {
    q: "Hva skjer etter 30 dager?",
    a: "Tilgangen din utløper etter 30 dager. Du kan forny for 79 kr for å få tilgang til 30 nye dager. Ingen automatisk trekk — du bestemmer selv.",
  },
  {
    q: "Kan jeg prøve gratis først?",
    a: "Ja! Du får gratis tilgang til 10 spørsmål per dag uten å betale noe. Betal kun når du er klar for fullstendig tilgang.",
  },
  {
    q: "Hva er innholdet basert på?",
    a: "Alt innhold er basert på offisielle kilder fra Statens vegvesen og gjeldende trafikkregler i Norge.",
  },
  {
    q: "Kan jeg få pengene tilbake?",
    a: "Ja. Etter norsk angrerett har du 14 dagers angrefrist. Kontakt oss på support@veiklar.no så ordner vi det.",
  },
  {
    q: "Er det trygt å betale med Vipps?",
    a: "Vipps er Norges mest brukte betalingsapp med over 4 millioner brukere. Kortinformasjonen din lagres aldri hos VeiKlar.",
  },
];

function FAQ({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        className="w-full flex items-center justify-between px-5 py-4 text-left font-medium text-gray-800 hover:bg-gray-50 transition-colors"
        onClick={() => setOpen(!open)}
      >
        <span>{q}</span>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400 shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />}
      </button>
      {open && (
        <div className="px-5 pb-4 text-sm text-gray-600 leading-relaxed border-t border-gray-100 pt-3">
          {a}
        </div>
      )}
    </div>
  );
}

export default function Pricing() {
  const { user, loginWithVipps } = useAuth();
  const navigate = useNavigate();

  const handleGetAccess = () => {
    if (!user) loginWithVipps();
    else navigate("/checkout");
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <main className="max-w-4xl mx-auto px-4 py-16">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-green-50 text-green-700 text-xs font-semibold rounded-full mb-4 border border-green-200">
            <Shield className="w-3.5 h-3.5" /> Basert på offisielle Statens vegvesen-kilder
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-3">
            Bestå teoriprøven første gang
          </h1>
          <p className="text-gray-500 max-w-md mx-auto">
            Tilgang til alle spørsmål, prøveeksamener og forklaringer — betal kun for månedene du trenger.
          </p>
        </div>

        {/* Cards */}
        <div className="grid sm:grid-cols-2 gap-6 mb-10">
          {/* Free */}
          <div className="bg-white border border-gray-200 rounded-2xl p-6">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-1">Gratis</h2>
              <div className="text-3xl font-bold text-gray-900">0 kr</div>
              <p className="text-sm text-gray-500 mt-1">Begrenset daglig tilgang</p>
            </div>
            <ul className="space-y-2.5 mb-6">
              {FREE_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-gray-600">
                  <Check className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
                  {f}
                </li>
              ))}
            </ul>
            <Button variant="outline" className="w-full" onClick={() => navigate("/dashboard")}>
              Fortsett gratis
            </Button>
          </div>

          {/* Premium */}
          <div className="bg-fjord text-white rounded-2xl p-6 relative overflow-hidden shadow-lg shadow-fjord/20">
            <div className="absolute top-0 right-0 bg-yellow-400 text-yellow-900 text-xs font-bold px-3 py-1 rounded-bl-xl">
              ANBEFALT
            </div>
            <div className="mb-4">
              <h2 className="text-lg font-semibold mb-1">Full tilgang</h2>
              <div className="flex items-end gap-2">
                <span className="text-4xl font-bold">{MONTHLY_PRICE} kr</span>
                <span className="text-blue-200 text-sm mb-1">/ måned</span>
              </div>
              <p className="text-blue-200 text-sm mt-1">30 dager · ingen automatisk fornyelse</p>
            </div>
            <ul className="space-y-2.5 mb-6">
              {PREMIUM_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-blue-100">
                  <Check className="w-4 h-4 text-yellow-400 shrink-0 mt-0.5" />
                  {f}
                </li>
              ))}
            </ul>
            <button
              onClick={handleGetAccess}
              className="w-full bg-white text-fjord font-semibold rounded-xl py-3 hover:bg-blue-50 transition-colors"
            >
              {user ? "Få full tilgang →" : "Logg inn og få tilgang →"}
            </button>
            <p className="text-center text-xs text-blue-200 mt-3">
              14 dagers angrerett · ingen skjulte kostnader
            </p>
          </div>
        </div>

        {/* Trust row */}
        <div className="flex flex-wrap items-center justify-center gap-6 py-6 border-y border-gray-200 mb-10">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <Shield className="w-4 h-4 text-green-600" />
            Sikker betaling via Vipps
          </div>
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <RefreshCw className="w-4 h-4 text-fjord" />
            14 dagers angrerett
          </div>
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <Star className="w-4 h-4 text-yellow-500" />
            Ingen automatisk fornyelse
          </div>
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <Lock className="w-4 h-4 text-gray-600" />
            Kortinfo lagres aldri
          </div>
        </div>

        {/* Feature highlights */}
        <div className="mb-12 grid grid-cols-3 gap-4 text-center">
          {[
            { icon: BookOpen, label: "Offisielle kilder", sub: "Alt innhold fra Statens vegvesen" },
            { icon: ClipboardList, label: "Ekte eksamensformat", sub: "45 spørsmål, 90 min, 38 for å bestå" },
            { icon: BarChart2, label: "Spor fremdriften din", sub: "Se svake tema med en gang" },
          ].map(({ icon: Icon, label, sub }) => (
            <div key={label} className="bg-white border border-gray-100 rounded-xl p-4">
              <Icon className="w-6 h-6 text-fjord mx-auto mb-2" />
              <div className="text-sm font-semibold text-gray-800">{label}</div>
              <div className="text-xs text-gray-500 mt-0.5">{sub}</div>
            </div>
          ))}
        </div>

        {/* Testimonials */}
        <div className="mb-12">
          <h2 className="text-xl font-bold text-gray-900 text-center mb-6">Hva brukerne sier</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              {
                name: "Amina H.",
                flag: "🇸🇴",
                text: "Jeg besto teoriprøven første gang etter 3 uker med VeiKlar. Spørsmålene på somali hjalp meg veldig!",
                stars: 5,
              },
              {
                name: "Khalid A.",
                flag: "🇸🇾",
                text: "Endelig en app på arabisk! Forklaringene er enkle og det er lett å forstå norske trafikkregler.",
                stars: 5,
              },
              {
                name: "Fatuma M.",
                flag: "🇪🇹",
                text: "79 kr er veldig rimelig. Jeg brukte appen i to måneder og besto eksamen. Anbefales!",
                stars: 5,
              },
            ].map(({ name, flag, text, stars }) => (
              <div key={name} className="bg-white border border-gray-200 rounded-xl p-5">
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: stars }).map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-sm text-gray-700 mb-3 leading-relaxed">"{text}"</p>
                <div className="text-xs font-semibold text-gray-500">{flag} {name}</div>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div className="mb-12">
          <h2 className="text-xl font-bold text-gray-900 text-center mb-6">Ofte stilte spørsmål</h2>
          <div className="space-y-3">
            {FAQS.map((faq) => (
              <FAQ key={faq.q} {...faq} />
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="bg-fjord text-white rounded-2xl p-8 text-center">
          <h2 className="text-2xl font-bold mb-2">Klar til å bestå?</h2>
          <p className="text-blue-200 mb-6">Kom i gang gratis — betal kun når du trenger full tilgang</p>
          <button
            onClick={handleGetAccess}
            className="bg-white text-fjord font-semibold rounded-xl px-8 py-3 hover:bg-blue-50 transition-colors inline-block"
          >
            {user ? `Få tilgang — ${MONTHLY_PRICE} kr/mnd →` : "Logg inn med Vipps →"}
          </button>
          <p className="text-xs text-blue-300 mt-3">14 dagers angrerett · ingen automatisk fornyelse</p>
        </div>
      </main>
    </div>
  );
}
